/**
 * @file report-date-presets.ts
 * @module app/modules/uProcure/inventory-replenishment/report-date-presets
 * @description Quick date ranges for the Inventory Replenishment report (all end today at the latest, at most 366
 *   days) and the range check used by the picker.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

export const MAX_RANGE_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface DatePreset {
  key: string;
  label: string;
  range: (today: Date) => { start: Date; end: Date };
}

/** Midnight (local time) of a date. */
export const dayStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Days in a range, both ends included. */
export const daysInRange = (start: Date, end: Date): number =>
  Math.round((dayStart(end).getTime() - dayStart(start).getTime()) / DAY_MS) + 1;

const lastDays = (n: number) => (today: Date) => {
  const end = dayStart(today);
  return { start: new Date(end.getFullYear(), end.getMonth(), end.getDate() - (n - 1)), end };
};

export const DATE_PRESETS: DatePreset[] = [
  { key: "7d", label: "Last 7 days", range: lastDays(7) },
  { key: "30d", label: "Last 30 days", range: lastDays(30) },
  { key: "90d", label: "Last 90 days", range: lastDays(90) },
  { key: "180d", label: "Last 180 days", range: lastDays(180) },
  { key: "366d", label: "Last 1 year", range: lastDays(MAX_RANGE_DAYS) },
  {
    key: "month",
    label: "This month",
    range: (today) => ({ start: new Date(today.getFullYear(), today.getMonth(), 1), end: dayStart(today) }),
  },
  {
    key: "lastMonth",
    label: "Last month",
    range: (today) => ({
      start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      end: new Date(today.getFullYear(), today.getMonth(), 0),
    }),
  },
  {
    key: "ytd",
    label: "This year",
    range: (today) => ({ start: new Date(today.getFullYear(), 0, 1), end: dayStart(today) }),
  },
];

/** The preset matching a range exactly (to highlight it), if any. */
export const matchingPreset = (start: Date | null, end: Date | null, today: Date): string | null => {
  if (!start || !end) return null;
  const hit = DATE_PRESETS.find((p) => {
    const r = p.range(today);
    return dayStart(r.start).getTime() === dayStart(start).getTime() && dayStart(r.end).getTime() === dayStart(end).getTime();
  });
  return hit ? hit.key : null;
};

/** Latest end date the picker allows: 366 days from the start, never after today. */
export const maxEndDate = (start: Date | null, end: Date | null, today: Date): Date => {
  const t = dayStart(today);
  if (start && !end) {
    const limit = new Date(start.getFullYear(), start.getMonth(), start.getDate() + MAX_RANGE_DAYS - 1);
    return limit < t ? limit : t;
  }
  return t;
};

/** Error message for a range, or null when it is valid. */
export const rangeError = (start: Date | null, end: Date | null): string | null => {
  if (!start || !end) return "Choose a start and end date";
  if (dayStart(end) < dayStart(start)) return "The end date is before the start date";
  if (daysInRange(start, end) > MAX_RANGE_DAYS) return `The range can be at most ${MAX_RANGE_DAYS} days`;
  return null;
};
